// src/components/CardLinkPage.js — PUBLIC page behind https://iscoopcrm.com/c/<code> (routed in src/index.js).
// A customer opens the short link an operator texted them and saves a card: Stripe Card Element on a
// SetupIntent on the OPERATOR'S connected account (no Stripe Link, so no verification codes; no login).
// Shows first name, plan and price — nothing else. Backend: public-card-link (lookup/complete), sql/090.
import { useEffect, useRef, useState } from "react";
import { SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadStripe } from "../lib/stripe";

const FN = `${SUPABASE_URL}/functions/v1/public-card-link`;

async function callLink(body) {
  const r = await fetch(FN, {
    method: "POST",
    headers: { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, ...d };
}

const BRAND_LABEL = { visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay" };
const brandLabel = (b) => BRAND_LABEL[b] || (b ? b[0].toUpperCase() + b.slice(1) : "Card");
const fmtExpiry = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export function CardLinkPage({ code }) {
  // loading | expired | used | ready | saving | done | failed
  const [phase, setPhase] = useState("loading");
  const [info, setInfo] = useState({});
  const [err, setErr] = useState("");
  const [cardError, setCardError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const mountedRef = useRef(false);
  const secretRef = useRef("");
  const succeededSiRef = useRef(""); // a SetupIntent that already succeeded → never re-confirm, just complete

  useEffect(() => {
    let cancelled = false;
    callLink({ action: "lookup", code }).then((d) => {
      if (cancelled) return;
      setInfo(d);
      if (d.state === "ready") { secretRef.current = d.client_secret; setPhase("ready"); }
      else if (d.state === "used") setPhase("used");
      else if (d.state === "rate_limited" || d.state === "error") { setErr(d.error || "Please try again in a moment."); setPhase("failed"); }
      else setPhase("expired");
    }).catch(() => {
      if (!cancelled) { setErr("We couldn't load this page. Check your connection and try again."); setPhase("failed"); }
    });
    return () => { cancelled = true; };
  }, [code]);

  useEffect(() => {
    if (info.org_name) document.title = `${info.org_name} · Add a card`;
  }, [info.org_name]);

  // Mount the Card Element once, when the form first appears. It stays mounted through failed save attempts
  // (ready → saving → ready), so the customer never retypes a card because of a decline.
  useEffect(() => {
    if (phase !== "ready" || mountedRef.current || !info.stripe_account_id) return;
    let cancelled = false;
    loadStripe(info.stripe_account_id).then((stripe) => {
      if (cancelled) return;
      stripeRef.current = stripe;
      const card = stripe.elements().create("card", {
        hidePostalCode: true,
        disableLink: true, // no Stripe Link "Autofill" button — Link is what asks customers for a verification code
        style: {
          // 16px: under 16 iOS zooms when the card iframe input is focused.
          base: { fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif", fontSize: "16px", color: "#0f172a", "::placeholder": { color: "#94a3b8" } },
          invalid: { color: "#b91c1c" },
        },
      });
      card.mount("#card-link-element");
      card.on("change", (e) => { setCardError(e.error?.message || ""); setCardComplete(!!e.complete); });
      cardRef.current = card;
      mountedRef.current = true;
      setStripeReady(true);
    }).catch(() => {
      if (!cancelled) setErr("Couldn't load the secure card form. Please refresh the page and try again.");
    });
    return () => { cancelled = true; };
  }, [phase, info.stripe_account_id]);

  useEffect(() => () => { try { cardRef.current?.destroy?.(); } catch { /* already gone */ } }, []);

  async function save(e) {
    e.preventDefault();
    if (phase !== "ready" || !stripeRef.current || !cardRef.current) return;
    if (!succeededSiRef.current && !cardComplete) { setErr("Finish your card details first."); return; }
    setErr("");
    setPhase("saving");
    try {
      let siId = succeededSiRef.current;
      if (!siId) {
        const { setupIntent, error } = await stripeRef.current.confirmCardSetup(secretRef.current, {
          payment_method: { card: cardRef.current, billing_details: { name: info.first_name || undefined } },
        });
        if (error) {
          setErr(error.message || "Your card couldn't be saved. Check the details and try again.");
          setPhase("ready");
          return;
        }
        siId = setupIntent.id;
        succeededSiRef.current = siId;
      }
      const d = await callLink({ action: "complete", code, setup_intent_id: siId });
      if (d.state === "done" || d.state === "used") {
        setInfo((i) => ({ ...i, brand: d.brand || i.brand, last4: d.last4 || i.last4 }));
        setPhase("done");
        return;
      }
      setErr(d.error || "Your card reached Stripe but we couldn't confirm it. Please tap Save card again.");
      setPhase("ready");
    } catch {
      setErr("Something went wrong. Please try again.");
      setPhase("ready");
    }
  }

  const org = info.org_name || "your service provider";
  const showForm = phase === "ready" || phase === "saving";

  return (
    <div style={S.page}>
      <main style={S.card}>
        <header style={S.head}>
          <div style={S.eyebrow}>Secure card update</div>
          <div style={S.brand}>{org}</div>
        </header>

        {phase === "loading" && <p style={S.muted}>Loading…</p>}

        {phase === "expired" && (
          <>
            <div style={S.bigIcon} aria-hidden="true">⌛</div>
            <h1 style={S.h1}>This link has expired</h1>
            <p style={S.muted}>Card links work for 24 hours and can only be used once. Ask {org} to send you a fresh one.</p>
          </>
        )}

        {phase === "used" && (
          <>
            <div style={S.bigIcon} aria-hidden="true">✅</div>
            <h1 style={S.h1}>Card already saved</h1>
            <p style={S.muted}>
              {info.last4 ? `${brandLabel(info.brand)} ending in ${info.last4} was added with this link.` : "A card was already added with this link."}
              {" "}Need to change it? Ask {org} for a new link.
            </p>
          </>
        )}

        {phase === "failed" && (
          <>
            <div style={S.bigIcon} aria-hidden="true">⚠️</div>
            <h1 style={S.h1}>Something went wrong</h1>
            <p style={S.muted}>{err}</p>
          </>
        )}

        {showForm && (
          <form onSubmit={save} noValidate>
            <h1 style={S.h1}>Hi {info.first_name}! 👋</h1>
            <p style={S.muted}>
              Add the card you'd like {org} to keep on file
              {info.plan ? <> for your <strong style={S.strong}>{info.plan}</strong></> : null}.
              {info.price ? <> Billed monthly: <strong style={S.strong}>{info.price}</strong>.</> : null}
            </p>
            {info.current_card?.last4 && (
              <p style={S.note}>This replaces the {brandLabel(info.current_card.brand)} ending in {info.current_card.last4}.</p>
            )}

            <label style={S.label} htmlFor="card-link-element">Card details</label>
            <div id="card-link-element" style={S.cardField} />
            {!stripeReady && !err && <p style={S.hint}>Loading the secure card form…</p>}
            {cardError && <p style={S.inlineErr}>{cardError}</p>}
            {err && <div style={S.errBox} role="alert" aria-live="assertive">⚠️ {err}</div>}

            <button type="submit" style={{ ...S.btn, opacity: phase === "saving" || !stripeReady ? 0.7 : 1 }} disabled={phase === "saving" || !stripeReady}>
              {phase === "saving" ? "Saving your card…" : "Save card"}
            </button>
            <p style={S.trust}>🔒 Secured by Stripe — {org} never sees your full card number. Saving a card doesn't charge it; it's used for your regular billing.</p>
          </form>
        )}

        {phase === "done" && (
          <>
            <div style={S.bigIcon} aria-hidden="true">✅</div>
            <h1 style={S.h1}>Card saved</h1>
            <p style={S.muted}>
              {brandLabel(info.brand)} ending in {info.last4} is now the card on file{info.plan ? ` for your ${info.plan}` : ""} with {org}. You can close this page.
            </p>
          </>
        )}

        <footer style={S.foot}>
          {showForm && info.expires_at ? <span>Link expires {fmtExpiry(info.expires_at)} · </span> : null}
          <span>Powered by iScoopCRM</span>
        </footer>
      </main>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "#f6f7fb", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "28px 14px 40px", boxSizing: "border-box", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", color: "#0f172a" },
  card: { width: "100%", maxWidth: 440, background: "#fff", borderRadius: 20, padding: "24px 22px 18px", boxShadow: "0 20px 60px -20px rgba(40,40,90,.25)", border: "1px solid #eef0f6", boxSizing: "border-box" },
  head: { marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #eef0f6" },
  eyebrow: { fontSize: 11.5, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: "#6366f1" },
  brand: { fontSize: 19, fontWeight: 800, color: "#0f172a", marginTop: 4 },
  h1: { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "4px 0 10px", lineHeight: 1.2 },
  muted: { color: "#475569", fontSize: 14.5, lineHeight: 1.55, margin: "0 0 6px" },
  strong: { color: "#0f172a", fontWeight: 700 },
  note: { fontSize: 12.5, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", margin: "10px 0 0", lineHeight: 1.45 },
  label: { display: "block", fontSize: 12.5, fontWeight: 600, color: "#334155", margin: "16px 2px 6px" },
  cardField: { padding: "13px 13px", border: "1px solid #d7dbe6", borderRadius: 11, background: "#fff", minHeight: 46, boxSizing: "border-box" },
  hint: { fontSize: 11.5, color: "#64748b", margin: "6px 2px 0" },
  inlineErr: { fontSize: 12, color: "#b91c1c", margin: "6px 2px 0" },
  errBox: { fontSize: 13, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", margin: "12px 0 0" },
  btn: { width: "100%", marginTop: 16, padding: "14px", border: "none", borderRadius: 12, cursor: "pointer", fontSize: 15.5, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", boxShadow: "0 10px 26px -10px rgba(99,102,241,.6)" },
  trust: { fontSize: 11.5, color: "#64748b", lineHeight: 1.5, margin: "12px 2px 0" },
  bigIcon: { fontSize: 40, lineHeight: 1, margin: "6px 0 10px" },
  foot: { marginTop: 18, paddingTop: 12, borderTop: "1px solid #eef0f6", fontSize: 11, color: "#94a3b8", textAlign: "center" },
};
